<template>
  <div class="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
    <AiAnalysisDocumentPreview :documents="documents" class="max-h-[85vh] overflow-y-auto lg:sticky lg:top-4" />

    <div class="mt-6 lg:mt-0">
      <span :class="badgeClassFor(verdict)" class="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold">
        {{ verdictLabel }}
      </span>

      <div v-if="errorMessage" class="mt-4 rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
        <p class="font-semibold">{{ t("unavailable") }}</p>
        <p class="mt-1">{{ errorMessage }}</p>
      </div>

      <div
        v-else-if="!analyzed"
        class="mt-4 rounded-md border-l-4 border-gray-300 bg-gray-50 p-4 text-sm text-gray-700"
      >
        {{ t("not_analyzed") }}
      </div>

      <div v-else class="mt-4">
        <div v-if="hasSomethingToVerify">
          <h3 class="text-sm font-semibold text-gray-800">{{ t("to_verify") }}</h3>
          <p v-if="summary" class="mt-2 whitespace-pre-line text-sm text-gray-700">{{ summary }}</p>
          <ul v-else class="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
            <li v-for="check in checksToVerify" :key="check.key">
              <span class="font-medium">{{ check.label }}</span>
              <span v-if="check.reason">— {{ check.reason }}</span>
            </li>
          </ul>
        </div>
        <p v-else class="text-sm text-green-700">{{ t("nothing_to_report") }}</p>

        <div v-if="hasDetail" class="mt-4">
          <button
            type="button"
            class="text-xs text-gray-500 underline hover:text-gray-700 focus:outline-hidden"
            @click="detailsOpen = !detailsOpen"
          >
            {{ detailsOpen ? t("see_less") : t("see_more") }}
          </button>

          <div v-if="detailsOpen" class="mt-3">
            <div v-if="detailSections.length > 0" class="space-y-6">
              <VerificationTable
                v-for="section in detailSections"
                :key="section.key"
                :title="section.title"
                :description="section.description"
                :checks="section.checks"
              />
            </div>

            <table v-else class="w-full text-left text-sm">
              <thead class="border-b border-gray-200 text-xs uppercase text-gray-500">
                <tr>
                  <th class="py-2 pr-4">{{ t("column_field") }}</th>
                  <th class="py-2 pr-4">{{ t("column_verdict") }}</th>
                  <th class="py-2">{{ t("column_reason") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="check in checks" :key="check.key" class="border-b border-gray-100">
                  <td class="py-2 pr-4 font-medium text-gray-700">{{ check.label }}</td>
                  <td class="py-2 pr-4">
                    <span
                      :class="badgeClassFor(check.verdict)"
                      class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold"
                    >
                      {{ verdictLabelFor(check.verdict) }}
                    </span>
                  </td>
                  <td class="py-2 text-gray-600">{{ check.reason }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <AiAnalysisFeedbackForm
          class="mt-6 border-t border-gray-100 pt-4"
          analysable-type="Claims::Declaration"
          :analysable-id="analysableId"
          :analysis-type="analysisType"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import AiAnalysisDocumentPreview from "@/components/claim/details/ai-analysis-flow/AiAnalysisDocumentPreview.vue";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";
import VerificationTable from "@/components/claim/details/VerificationTable.vue";
import { badgeClassFor, isToVerify } from "@/components/claim/details/ai-analysis-flow/claimAiAnalysisSummary";

export default {
  name: "AiAnalysisDocumentStep",

  components: { AiAnalysisDocumentPreview, AiAnalysisFeedbackForm, VerificationTable },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    analysisType: {
      type: String,
      required: true,
    },
    analysis: {
      type: Object,
      default: null,
    },
    analyzedAt: {
      type: [String, Date],
      default: null,
    },
    verdict: {
      type: String,
      default: null,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    checks: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: String,
      default: "",
    },
    detailSections: {
      type: Array,
      default: () => [],
    },
    analysableId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      detailsOpen: false,
    };
  },

  computed: {
    errorMessage() {
      return this.analysis?.error || null;
    },
    analyzed() {
      return Boolean(this.analyzedAt) && !this.errorMessage;
    },
    checksToVerify() {
      return this.checks.filter((check) => isToVerify(check.verdict));
    },
    hasSomethingToVerify() {
      return this.checksToVerify.length > 0 || Boolean(this.summary);
    },
    hasDetail() {
      return this.detailSections.length > 0 || this.checks.length > 0;
    },
    verdictLabel() {
      return this.verdictLabelFor(this.verdict);
    },
  },

  methods: {
    badgeClassFor,
    verdictLabelFor(verdict) {
      const key = `verdict.${verdict}`;
      const label = this.t(key);
      return label === key ? this.t("verdict.unavailable") : label;
    },
  },
};
</script>

<i18n src="./AiAnalysisDocumentStep.i18n.json"></i18n>
